import './styles.css';
import { Capacitor } from '@capacitor/core';
import { App as NativeApp } from '@capacitor/app';
import { createGame, startHand, getLegalActions, act, PHASE, assertIntegrity, careerSnapshot, formatMoney } from './game/game.js';
import { chooseAction, PERSONALITIES } from './game/ai.js';
import { cardParts, cardLabel } from './game/cards.js';
import { evaluateHand } from './game/evaluator.js';
import { CURRENCY_PROFILES, DENOMINATIONS, RAILROAD_UNLOCK, TABLE_MODES, allocateCurrency, piecesFromInventory, reallocateCurrency } from './game/currency.js';
import { actionCue, aiActionHold, aiThinkDelay, atmosphereFor, handGuideCards, streetPresentation, tableSoundForTransition, tabletopPropFor } from './game/presentation.js';
import { AudioDirector } from './audio.js';

const app = document.querySelector('#app');
const audio = new AudioDirector();
const CAREER_KEY = 'poker-career-v2';
function loadCareer() {
  try { return JSON.parse(localStorage.getItem(CAREER_KEY) || 'null') || {}; } catch { return {}; }
}
let savedCareer = loadCareer();
let state = createGame(undefined, Math.random, savedCareer);
savedCareer = careerSnapshot(state);
let aiTimer = null;
let aiActionTimer = null;
let aiActionBeat = null;
let applicationPaused = false;
let introOpen = true;
let settingsOpen = false;
let helpOpen = false;
let currencyOpen = false;
let reducedMotion = localStorage.getItem('poker-reduced-motion') === 'true';
let largeCards = localStorage.getItem('poker-large-cards') === 'true';
let handGuideEnabled = localStorage.getItem('poker-hand-guide') !== 'false';
let renderedCommunityCount = 0;

const mediaRoot = new URL(`${import.meta.env.BASE_URL}media/`, document.baseURI);
const media = path => `url("${new URL(path, mediaRoot).href}")`;
const mediaVariables = {
  '--media-saloon': media('saloon-pov.webp'),
  '--media-materials': media('material-atlas.webp'),
  '--media-props': media('props-atlas.webp'),
  '--media-stakes-effects': media('stakes-effects-atlas-v1.webp'),
  '--media-buttons': media('saloon-button-plates-v1.webp'),
  '--media-icon-button': media('saloon-icon-button-v1.webp'),
  '--media-robbery': media('robbery-atlas-v1.webp'),
  ...Object.fromEntries(['mara', 'mateo', 'isaiah', 'mei', 'finn', 'eli'].map(id => [`--media-patron-${id}`, media(`patrons/${id}.webp`)])),
  ...Object.fromEntries(['indian-cent', 'seated-dime', 'seated-quarter', 'seated-half', 'morgan-dollar', 'double-eagle'].map(id => [`--media-coin-${id}`, media(`coins/${id}.webp`)])),
  ...Object.fromEntries(['one-dollar', 'five-dollar', 'ten-dollar', 'twenty-dollar', 'fifty-dollar', 'one-hundred-dollar', 'five-hundred-dollar', 'one-thousand-dollar'].map(id => [`--media-note-${id}`, media(`notes/${id}.webp`)]))
};
Object.entries(mediaVariables).forEach(([name, value]) => document.documentElement.style.setProperty(name, value));

const escapeHtml = value => String(value).replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));

function wagerPieces(source, limit = 10, profile = 'balanced') {
  const inventory = typeof source === 'number' ? allocateCurrency(source, profile) : source;
  const pieces = piecesFromInventory(inventory, limit);
  return pieces.map((piece, index) => {
    const tilt = ((index * 7 + piece.value) % 13) - 6;
    const stagger = ((index * 11 + piece.value) % 9) - 4;
    const view = (index * 5 + Math.round(piece.value / 7)) % piece.views;
    return `<i class="wager-piece ${piece.kind} ${piece.id} ${piece.kind}-view-${view}" style="--i:${index};--tilt:${tilt}deg;--stagger:${stagger}px;--spread:${stagger * 2}px" title="${piece.name}; ${formatMoney(piece.value)}"></i>`;
  }).join('');
}

function pursePileMarkup(inventory) {
  const notes = piecesFromInventory(inventory, 7, 'note');
  const coins = piecesFromInventory(inventory, 11, 'coin');
  const render = (piece, index, layer) => {
    const view = (index * 3 + piece.value) % piece.views;
    const tilt = layer === 'note' ? ((index * 7) % 11) - 5 : ((index * 5) % 15) - 7;
    const shift = ((index * 13) % 15) - 7;
    return `<i class="purse-piece ${layer} ${piece.id} ${piece.kind}-view-${view}" style="--i:${index};--tilt:${tilt}deg;--shift:${shift}px" title="${piece.name}"></i>`;
  };
  return `<div class="physical-purse" aria-hidden="true"><div class="purse-notes">${notes.map((piece, index) => render(piece, index, 'note')).join('')}</div><div class="purse-coins">${coins.map((piece, index) => render(piece, index, 'coin')).join('')}</div></div>`;
}

function cardMarkup(card, index, { hidden = false, selectable = false, selected = false, small = false, fan = null, guide = false, board = false, revealIndex = null } = {}) {
  const fanStyle = fan ? ` style="--fan-angle:${fan.angle}deg;--fan-y:${fan.y}px;--fan-x:${fan.x}px"` : '';
  const classes = `${small ? 'small ' : ''}${board ? 'board-card ' : ''}${guide ? 'hand-guide-match ' : ''}${revealIndex !== null ? 'card-reveal ' : ''}`;
  const revealStyle = revealIndex !== null ? `${fanStyle ? fanStyle.slice(0, -1) + ';' : ' style="'}--reveal-index:${revealIndex}"` : fanStyle;
  if (hidden) return `<div class="playing-card card-back ${classes}"${revealStyle} role="img" aria-label="Face-down card"><span class="back-medallion">EG</span></div>`;
  const info = cardParts(card);
  const label = cardLabel(card);
  return `<button class="playing-card card-face ${info.color} ${classes}${selected ? 'selected' : ''}"${revealStyle} ${selectable ? `data-card-index="${index}"` : 'disabled'} aria-label="${escapeHtml(label)}${guide ? `, part of your strongest hand` : ''}"${selectable ? ` aria-pressed="${selected}"` : ''}>
    <span class="corner top"><b>${info.displayRank}</b><i>${info.symbol}</i></span>
    <span class="pip">${info.symbol}</span>
    <span class="corner bottom"><b>${info.displayRank}</b><i>${info.symbol}</i></span>
  </button>`;
}

function opponentMarkup(seat, position) {
  const show = state.phase === PHASE.SHOWDOWN && !seat.folded;
  const isWinner = state.result?.winners?.includes(seat.id);
  const actionBeat = aiActionBeat?.seatId === seat.id ? aiActionBeat : null;
  const isActing = state.actingSeat === seat.id && !aiActionBeat;
  const profile = PERSONALITIES[seat.aiIndex];
  const emotion = state.result
    ? (isWinner ? (seat.stack % 2 ? 'happy' : 'pleased') : (seat.stack < 1000 ? 'angry' : 'sad'))
    : actionBeat ? (actionBeat.type === 'FOLD' ? 'sad' : ['BET', 'RAISE', 'ALL_IN'].includes(actionBeat.type) ? 'anticipation' : 'neutral')
    : isActing ? 'thinking'
    : /RAISES|BETS|ALL IN/.test(seat.lastAction) ? 'anticipation'
        : /FOLDS/.test(seat.lastAction) ? 'sad'
          : /TAKES A SEAT/.test(seat.lastAction) ? 'pleased' : 'neutral';
  const cards = seat.cards.length ? seat.cards.map((card, index) => {
    const seed = state.handNumber * 37 + seat.id * 19 + index * 11 + card.charCodeAt(0) * 3 + card.charCodeAt(1);
    const fan = { angle: ((seed % 5) - 2) * 0.55, y: (seed * 7) % 5 - 2, x: (seed * 11) % 3 - 1 };
    return cardMarkup(card, index, { hidden: !show, small: true, fan });
  }).join('') : '';
  return `<section class="opponent opponent-${position} ${seat.folded ? 'folded' : ''} ${isActing ? 'acting' : ''} ${actionBeat ? 'playing-action' : ''} ${isWinner ? 'winner' : ''}" aria-label="${profile.name}, ${profile.title}">
    <div class="character patron-${profile.id} emotion-${emotion}" aria-hidden="true"></div>
    <div class="opponent-cards">${cards}</div>
    <div class="nameplate">
      <div><strong>${profile.name}</strong><span>${profile.title}</span></div>
      <div class="seat-bank"><div class="wager-stack tiny">${wagerPieces(seat.stack, 8)}</div><b>${formatMoney(seat.stack)}</b></div>
    </div>
    ${state.dealer === seat.id ? '<div class="dealer-marker" title="Dealer">D</div>' : ''}
  </section>`;
}

function opponentActionMarkup(seat, position) {
  const actionBeat = aiActionBeat?.seatId === seat.id ? aiActionBeat : null;
  const isActing = state.actingSeat === seat.id && !aiActionBeat;
  const label = actionBeat?.label || seat.lastAction || (isActing ? 'THINKING…' : '');
  return `<div class="opponent-action opponent-action-${position}" aria-live="polite">${escapeHtml(label)}</div>`;
}

function currencyTrayMarkup(player) {
  const counts = player.purse;
  const selectedProfile = CURRENCY_PROFILES[player.currencyProfile] || CURRENCY_PROFILES.balanced;
  const placed = player.roundBet ? wagerPieces(player.roundCurrency, 9, player.currencyProfile) : '';
  return `<aside class="currency-tray ${currencyOpen ? 'open' : ''}" aria-label="Your period-currency purse">
    ${pursePileMarkup(counts)}
    <button type="button" class="currency-toggle" data-action="toggle-purse" aria-expanded="${currencyOpen}" aria-controls="currency-menu"><div><small>YOUR PURSE</small><strong>${formatMoney(player.stack)}</strong></div><span>${TABLE_MODES[state.tableMode].label}<br>${currencyOpen ? 'Close' : 'Reallocate'} ▸</span></button>
    <div class="currency-menu" id="currency-menu" role="dialog" aria-modal="false" aria-labelledby="currency-menu-title">
      <header class="currency-menu-header"><div><small class="sort-label">PERIOD CURRENCY</small><h2 id="currency-menu-title">Reallocate without changing value</h2></div><button type="button" class="currency-close" data-action="close-purse" aria-label="Close purse">×</button></header>
      <div class="profile-sorter" role="list" aria-label="Choose a preferred currency mixture">
        ${Object.values(CURRENCY_PROFILES).map(profile => `<button type="button" role="listitem" class="profile-choice ${profile.id === selectedProfile.id ? 'preferred' : ''}" data-currency-profile="${profile.id}" aria-pressed="${profile.id === selectedProfile.id}"><b>${profile.label}</b><span>${profile.description}</span></button>`).join('')}
      </div>
      <div class="denomination-ledger" aria-label="Exact purse denominations">
        ${DENOMINATIONS.map(piece => `<span><i class="ledger-swatch ${piece.kind} ${piece.id} ${piece.kind}-view-0" aria-hidden="true"></i><em>${piece.short}</em><b>×${counts[piece.id] || 0}</b></span>`).join('')}
      </div>
    </div>
    <p>${player.roundBet ? `Placed this round: ${formatMoney(player.roundBet)}` : selectedProfile.description}</p>
    ${placed ? `<div class="tray-wager wager-stack">${placed}</div>` : ''}
  </aside>`;
}

function communityBoardMarkup(guideCards, revealFrom) {
  const community = state.communityCards || [];
  const street = streetPresentation(state);
  const cardAt = index => community[index]
    ? cardMarkup(community[index], index, { board: true, guide: guideCards.has(community[index]), revealIndex: index >= revealFrom ? index - revealFrom : null })
    : '<div class="board-placeholder" aria-hidden="true"></div>';
  return `<section class="community-board street-${street.id}" aria-label="Community cards: ${street.headline}">
    <div class="community-card-groups">
      <div class="board-group flop-cards"><div>${[0, 1, 2].map(cardAt).join('')}</div></div>
      <div class="board-group turn-card"><div>${cardAt(3)}</div></div>
      <div class="board-group river-card"><div>${cardAt(4)}</div></div>
    </div>
  </section>`;
}

function actionMarkup() {
  if (aiActionBeat) return '<span class="waiting"><i></i> The move settles on the felt</span>';
  if (state.phase === PHASE.IDLE) return '<button class="primary-action" data-action="start">Take a seat</button>';
  if (state.phase === PHASE.SHOWDOWN) return `<button class="primary-action" data-action="next-hand">Deal next hand</button>`;
  if (state.phase === PHASE.GAME_OVER) return '<button class="primary-action" data-action="restart">Buy in again</button>';
  if (state.actingSeat === 0) {
    return getLegalActions(state, 0).map(action => `<button class="${action.type === 'FOLD' ? 'danger-action' : action.type === 'ALL_IN' ? 'danger-action all-in-action' : action.type === 'RAISE' || action.type === 'BET' ? 'primary-action' : 'secondary-action'}" data-poker-action="${action.type}">${action.label}</button>`).join('');
  }
  return '<span class="waiting"><i></i> The table is thinking</span>';
}

function resultMarkup() {
  if (!state.result) return '';
  if (state.robbery) return `<div class="result-banner robbery-banner" role="alert">
    <small>HOLDUP AT EMBER GULCH</small><strong>The saloon is robbed!</strong><span>Masked outlaws take <b class="display-money">${formatMoney(state.robbery.amount)}</b>. ${escapeHtml(state.result.title)}—and this robbery cannot happen again.</span>
  </div>`;
  return `<div class="result-banner ${state.result.winners.includes(0) ? 'player-won' : ''}" role="status">
    <small>SHOWDOWN</small><strong>${escapeHtml(state.result.title)}</strong><span>${escapeHtml(state.result.detail).replace(/(\$[\d,]+\.\d{2})/g, '<b class="display-money">$1</b>')}</span>
  </div>`;
}

function robberyMarkup() {
  if (!state.robbery) return '';
  return `<div class="robbery-scene" aria-hidden="true">
    <i class="robbery-stage robbery-stage-1"></i><i class="robbery-stage robbery-stage-2"></i><i class="robbery-stage robbery-stage-3"></i><i class="robbery-stage robbery-stage-4"></i>
  </div>`;
}

function modalMarkup() {
  if (introOpen) return `<div class="modal-scrim"><section class="intro-card" data-modal-panel role="dialog" aria-modal="true" aria-labelledby="game-title">
    <span class="eyebrow">INCOPREA PRESENTS</span>
    <h1 id="game-title">POKER</h1>
    <p class="intro-copy">Night has settled over Ember Gulch. Three strangers have kept a chair open—and none of them came to lose.</p>
    <div class="rule-row"><span>TEXAS HOLD’EM</span><span>ONE CONTINUOUS GAME</span><span>PERIOD CURRENCY</span></div>
    <p class="progression-copy">Begin with ${formatMoney(TABLE_MODES.frontier.buyIn)}. Build your purse to ${formatMoney(RAILROAD_UNLOCK)} to reach permanent Railroad Baron stakes.</p>
    <button class="primary-action enter-saloon" data-action="enter">Enter the saloon</button>
    <small>Skill and chance. No real-money gambling.</small>
  </section></div>`;
  if (settingsOpen) return `<div class="modal-scrim" data-action="close-modal"><section class="settings-card" data-modal-panel role="dialog" aria-modal="true" aria-label="Settings">
    <header><h2>Table settings</h2><button class="icon-button" data-action="close-modal" aria-label="Close settings">×</button></header>
    <label><span><b>Music</b><small>Original saloon score</small></span><input type="checkbox" data-setting="music" ${audio.musicEnabled ? 'checked' : ''}></label>
    <label><span><b>Table sounds</b><small>One soft cue for your buttons and rare scene events</small></span><input type="checkbox" data-setting="sfx" ${audio.sfxEnabled ? 'checked' : ''}></label>
    <label><span><b>Reduced motion</b><small>Quicker deals and no drifting smoke</small></span><input type="checkbox" data-setting="motion" ${reducedMotion ? 'checked' : ''}></label>
    <label><span><b>Large cards</b><small>Increase hand size</small></span><input type="checkbox" data-setting="cards" ${largeCards ? 'checked' : ''}></label>
    <label><span><b>Hand guide</b><small>Highlight the five cards making your strongest current hand.</small></span><input type="checkbox" data-setting="hand-guide" ${handGuideEnabled ? 'checked' : ''}></label>
  </section></div>`;
  if (helpOpen) return `<div class="modal-scrim" data-action="close-modal"><section class="help-card" data-modal-panel role="dialog" aria-modal="true" aria-label="How to play">
    <header><h2>Texas Hold’em</h2><button class="icon-button" data-action="close-modal" aria-label="Close help">×</button></header>
    <ol><li>Every player antes ${formatMoney(state.ante)} and receives two private cards, dealt one at a time around the table.</li><li>Bet pre-flop, then the dealer reveals three community cards together: the flop.</li><li>Bet again before the turn and river are revealed one card at a time, each followed by betting.</li><li>At showdown, the strongest five-card combination from your two cards and the five community cards wins.</li></ol>
    <p>Hand order: high card, pair, two pair, three of a kind, straight, flush, full house, four of a kind, straight flush.</p>
  </section></div>`;
  return '';
}

function render() {
  const player = state.seats[0];
  const atmosphere = atmosphereFor(state);
  const tabletopProp = tabletopPropFor(state);
  const visibleOpponents = state.seats.slice(1).filter(seat => !seat.sittingOut);
  const positions = visibleOpponents.length === 1 ? ['center'] : ['left', 'center', 'right'];
  const availableCards = [...player.cards, ...(state.communityCards || [])];
  const currentEvaluation = availableCards.length >= 5 && !player.folded ? evaluateHand(availableCards) : null;
  const guided = new Set(handGuideEnabled ? handGuideCards(currentEvaluation) : []);
  const communityCount = state.communityCards?.length || 0;
  if (communityCount < renderedCommunityCount) renderedCommunityCount = communityCount;
  const revealFrom = communityCount > renderedCommunityCount ? renderedCommunityCount : communityCount;
  const playerCards = player.cards.map((card, index) => {
    const seed = state.handNumber * 41 + index * 13 + card.charCodeAt(0) * 5 + card.charCodeAt(1);
    const fan = { angle: ((seed % 7) - 3) * 0.32, y: (seed * 5) % 5 - 2, x: (seed * 3) % 3 - 1 };
    return cardMarkup(card, index, { guide: guided.has(card), fan });
  }).join('');
  app.innerHTML = `<main class="game ${atmosphere.id} ${introOpen ? 'intro-open' : ''} ${reducedMotion ? 'reduce-motion' : ''} ${largeCards ? 'large-cards' : ''}">
    <div class="saloon-layer" aria-hidden="true"></div>
    <div class="light-layer" aria-hidden="true"></div>
    <div class="dust-layer" aria-hidden="true"></div>
    <div class="vignette" aria-hidden="true"></div>
    <header class="topbar">
      <div class="brand-lockup"><span class="ember-mark">♠</span><div><b>EMBER GULCH</b><small>TEXAS HOLD’EM</small></div></div>
    <div class="table-stats"><span><small>POT</small><b>${formatMoney(state.pot)}</b></span><span><small>HAND</small><b>${state.handNumber || '—'}</b></span></div>
      <nav><button class="icon-button" data-action="help" aria-label="How to play">?</button><button class="icon-button" data-action="settings" aria-label="Settings">⚙</button></nav>
    </header>

    ${visibleOpponents.map((seat, index) => opponentMarkup(seat, positions[index])).join('')}
    ${visibleOpponents.map((seat, index) => opponentActionMarkup(seat, positions[index])).join('')}

    <div class="stakes-effects" aria-hidden="true">${tabletopProp ? `<i class="stakes-prop stakes-tabletop stakes-${tabletopProp.id}"></i>${tabletopProp.id === 'cigar' ? '<i class="prop smoke stakes-smoke"></i>' : ''}` : ''}<i class="stakes-prop stakes-atmosphere stakes-lamp"></i><i class="stakes-prop stakes-atmosphere stakes-crowd-left"></i><i class="stakes-prop stakes-atmosphere stakes-crowd-right"></i><i class="stakes-prop stakes-atmosphere stakes-glow"></i></div>
    ${communityBoardMarkup(guided, revealFrom)}
    <div class="pot-visual" aria-label="Pot: ${formatMoney(state.pot)}"><div class="wager-stack pot-stack">${wagerPieces(state.potCurrency, 12)}</div></div>
    ${robberyMarkup()}
    ${resultMarkup()}

    <section class="player-area ${state.actingSeat === 0 ? 'your-turn' : ''}" aria-label="Your hand">
      <div class="hand-meta"><span class="play-description" aria-live="polite">${escapeHtml(state.message)}</span></div>
      <div class="player-cards">${playerCards}</div>
      ${state.dealer === 0 ? '<div class="dealer-marker player-dealer" title="You are dealer">D</div>' : ''}
    </section>
    ${currencyTrayMarkup(player)}
    <footer class="action-dock" aria-label="Poker actions">${actionMarkup()}</footer>
    <div class="legal">Fictional play • Period currency renditions • No cash value</div>
    ${state.promotion ? `<div class="promotion-toast"><b>${escapeHtml(state.promotion.title)}</b><span>${escapeHtml(state.promotion.detail)}</span></div>` : ''}
    ${modalMarkup()}
  </main>`;
  renderedCommunityCount = communityCount;
}

function commit(next, cue, playerInitiated = false, actionBeat = null) {
  clearTimeout(aiActionTimer);
  const previous = state;
  state = next;
  assertIntegrity(state);
  if ([PHASE.IDLE, PHASE.SHOWDOWN, PHASE.GAME_OVER].includes(state.phase)) savedCareer = careerSnapshot(state);
  else savedCareer = { ...savedCareer, currencyProfile: state.seats[0].currencyProfile, railroadUnlocked: state.railroadUnlocked, peakBankroll: Math.max(savedCareer.peakBankroll || 0, state.peakBankroll || 0), robberyOccurred: state.robberyOccurred };
  localStorage.setItem(CAREER_KEY, JSON.stringify(savedCareer));
  audio.setMood(atmosphereFor(state).music);
  const tableSound = tableSoundForTransition(previous, state, { playerInitiated, cue });
  if (tableSound) audio.cue(tableSound);
  aiActionBeat = actionBeat;
  render();
  if (aiActionBeat) {
    const revision = state.revision;
    aiActionTimer = setTimeout(() => {
      if (state.revision !== revision) return;
      aiActionBeat = null;
      render();
      scheduleAI();
    }, aiActionHold(aiActionBeat.type, reducedMotion));
  } else scheduleAI();
}

function scheduleAI() {
  clearTimeout(aiTimer);
  if (applicationPaused) return;
  if (aiActionBeat) return;
  if (introOpen || currencyOpen || settingsOpen || helpOpen) return;
  if (![PHASE.BET_PRE, PHASE.BET_FLOP, PHASE.BET_TURN, PHASE.BET_RIVER].includes(state.phase) || state.actingSeat === null || state.actingSeat === 0) return;
  const revision = state.revision;
  const delay = aiThinkDelay(Math.random, reducedMotion);
  aiTimer = setTimeout(() => {
    if (state.revision !== revision || state.actingSeat === 0) return;
    const seat = state.seats[state.actingSeat];
    const action = chooseAction({ seat, legal: getLegalActions(state), phase: state.phase, communityCards: state.communityCards });
    const next = act(state, seat.id, action);
    const fallbackLabel = String(action.label || action.type)
      .toUpperCase()
      .replace(/^CHECK$/, 'CHECKS')
      .replace(/^FOLD$/, 'FOLDS')
      .replace(/^CALL /, 'CALLS ')
      .replace(/^BET /, 'BETS ')
      .replace(/^RAISE /, 'RAISES ');
    commit(next, null, false, { seatId: seat.id, type: action.type, label: next.seats[seat.id].lastAction || fallbackLabel });
  }, delay);
}

app.addEventListener('click', async event => {
  if (currencyOpen && !event.target.closest('.currency-tray')) {
    currencyOpen = false;
    if (!event.target.closest('[data-action], [data-poker-action]')) {
      render();
      scheduleAI();
      return;
    }
  }
  const poker = event.target.closest('[data-poker-action]');
  if (poker) {
    if (aiActionBeat || state.actingSeat !== 0) return;
    const action = getLegalActions(state, 0).find(item => item.type === poker.dataset.pokerAction);
    if (action) commit(act(state, 0, action), actionCue(action.type), true);
    return;
  }
  const profileChoice = event.target.closest('[data-currency-profile]');
  if (profileChoice) {
    const next = structuredClone({ ...state, indexer: undefined });
    next.indexer = state.indexer;
    next.seats[0].currencyProfile = profileChoice.dataset.currencyProfile;
    next.seats[0].purse = reallocateCurrency(next.seats[0].purse, profileChoice.dataset.currencyProfile);
    next.revision += 1;
    commit(next);
    return;
  }
  const actionTarget = event.target.closest('[data-action]');
  if (!actionTarget) return;
  if (actionTarget.classList.contains('modal-scrim') && event.target !== actionTarget) return;
  const action = actionTarget.dataset.action;
  if (action === 'enter') {
    void audio.unlock();
    introOpen = false;
    commit(startHand(state));
  } else if (action === 'start' || action === 'next-hand') {
    void audio.unlock();
    commit(startHand(state));
  } else if (action === 'restart') {
    localStorage.removeItem(CAREER_KEY);
    state = createGame();
    savedCareer = careerSnapshot(state);
    commit(startHand(state));
  } else if (action === 'toggle-purse') { currencyOpen = !currencyOpen; render(); scheduleAI(); }
  else if (action === 'close-purse') { currencyOpen = false; render(); scheduleAI(); }
  else if (action === 'settings') { settingsOpen = true; render(); scheduleAI(); }
  else if (action === 'help' || action === 'history') { helpOpen = true; render(); scheduleAI(); }
  else if (action === 'close-modal') { settingsOpen = false; helpOpen = false; render(); scheduleAI(); }
});

app.addEventListener('change', event => {
  const setting = event.target.dataset.setting;
  if (!setting) return;
  if (setting === 'music') audio.setMusic(event.target.checked);
  if (setting === 'sfx') audio.setSfx(event.target.checked);
  if (setting === 'motion') {
    reducedMotion = event.target.checked;
    localStorage.setItem('poker-reduced-motion', reducedMotion);
  }
  if (setting === 'cards') {
    largeCards = event.target.checked;
    localStorage.setItem('poker-large-cards', largeCards);
  }
  if (setting === 'hand-guide') {
    handGuideEnabled = event.target.checked;
    localStorage.setItem('poker-hand-guide', handGuideEnabled);
  }
  render();
});

document.addEventListener('keydown', event => {
  if (event.key === 'Escape') closeTransientSurface();
});

function closeTransientSurface() {
  if (!settingsOpen && !helpOpen && !currencyOpen) return false;
  settingsOpen = false;
  helpOpen = false;
  currencyOpen = false;
  render();
  scheduleAI();
  return true;
}

async function setApplicationActive(active) {
  applicationPaused = !active;
  clearTimeout(aiTimer);
  clearTimeout(aiActionTimer);
  if (!active) {
    await audio.suspend();
    return;
  }
  await audio.resume();
  if (aiActionBeat) {
    aiActionBeat = null;
    render();
  }
  scheduleAI();
}

document.addEventListener('visibilitychange', () => {
  void setApplicationActive(!document.hidden);
});

if (Capacitor.isNativePlatform()) {
  void NativeApp.addListener('appStateChange', ({ isActive }) => {
    void setApplicationActive(isActive);
  });
  void NativeApp.addListener('backButton', () => {
    if (!closeTransientSurface()) void NativeApp.exitApp();
  });
}

if (!Capacitor.isNativePlatform() && 'serviceWorker' in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.register('./sw.js').catch(() => {});
}
render();
